import { Injectable, OnDestroy, computed, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ExperienceOrder, OrderService } from '@mocli/MocliCommonLib';
import { Subscription, filter } from 'rxjs';

import {
  OrderNavMetadata,
  OrderNavRouteMatch,
  OrderNavSectionKey,
  OrderNavStandalonePageKey,
  ORDER_NAV_SECTION_BY_KEY,
  isOrderNavSectionKey,
  resolveEnabledOptionalOrderNavSectionKeys,
  resolveGameTypeLabelFromConfiguration,
  resolveVisibleOrderNavSectionKeys,
  splitOrderTags
} from './order-nav-sections';

@Injectable({
  providedIn: 'root'
})
export class CurrentOrderContextService implements OnDestroy {
  private readonly routeMatchState = signal<OrderNavRouteMatch | null>(null);
  private readonly gameTypeLabelState = signal<string | null>(null);
  private readonly tagsState = signal<string[]>([]);
  private readonly enabledOptionalSectionKeysState = signal<OrderNavSectionKey[]>([]);
  private readonly loadingState = signal(false);

  private readonly metadataCache = new Map<number, OrderNavMetadata>();
  private readonly routerSubscription: Subscription;

  private requestSequence = 0;

  readonly isActive = computed(() => this.routeMatchState() !== null);
  readonly orderId = computed(() => this.routeMatchState()?.orderId ?? null);
  readonly sectionKey = computed(() => this.routeMatchState()?.sectionKey ?? null);
  // Page hors-section (ex: éditeur JSON admin, options de commande admin).
  readonly standalonePageKey = computed(() => this.routeMatchState()?.standalonePageKey ?? null);
  readonly isJsonEditor = computed(() => this.standalonePageKey() === 'jsonEditor');
  readonly isOrderOptionsPage = computed(() => this.standalonePageKey() === 'orderOptions');
  readonly isOverviewPage = computed(() => this.standalonePageKey() === 'overview');
  readonly isStatsExplorerPage = computed(() => this.standalonePageKey() === 'statsExplorer');
  // Libellé courant : libellé de la section, ou libellé personnalisé des pages hors-section.
  readonly currentLabel = computed(() => {
    const match = this.routeMatchState();
    if (!match) {
      return '';
    }
    return match.sectionKey ? ORDER_NAV_SECTION_BY_KEY[match.sectionKey].label : (match.label ?? '');
  });
  readonly currentSection = computed(() => {
    const key = this.sectionKey();
    return key ? ORDER_NAV_SECTION_BY_KEY[key] : null;
  });
  readonly visibleSections = computed(() => resolveVisibleOrderNavSectionKeys(
    this.enabledOptionalSectionKeysState(),
    this.sectionKey()
  ).map((key) => ORDER_NAV_SECTION_BY_KEY[key]));
  readonly gameTypeLabel = computed(() => this.gameTypeLabelState());
  readonly tags = computed(() => this.tagsState());
  readonly loading = computed(() => this.loadingState());

  constructor(
    private readonly router: Router,
    private readonly orderService: OrderService
  ) {
    this.consumeUrl(this.router.url);
    this.routerSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.consumeUrl(event.urlAfterRedirects || event.url));
  }

  ngOnDestroy(): void {
    this.routerSubscription.unsubscribe();
  }

  updateOrderTags(orderId: number, tags: string | null | undefined): void {
    const nextTags = splitOrderTags(tags);
    const cachedMetadata = this.metadataCache.get(orderId);
    if (cachedMetadata) {
      this.metadataCache.set(orderId, { ...cachedMetadata, tags: nextTags });
    }
    if (this.orderId() === orderId) {
      this.tagsState.set(nextTags);
    }
  }

  private consumeUrl(url: string): void {
    const nextRouteMatch = this.matchOrderRoute(url);
    const previousRouteMatch = this.routeMatchState();

    if (!nextRouteMatch) {
      this.resetState();
      return;
    }

    this.routeMatchState.set(nextRouteMatch);

    if (previousRouteMatch?.orderId === nextRouteMatch.orderId) {
      return;
    }

    const cachedMetadata = this.metadataCache.get(nextRouteMatch.orderId);
    if (cachedMetadata) {
      this.gameTypeLabelState.set(cachedMetadata.gameTypeLabel);
      this.tagsState.set(cachedMetadata.tags);
      this.enabledOptionalSectionKeysState.set(cachedMetadata.enabledOptionalSectionKeys);
      this.loadingState.set(false);
      return;
    }

    void this.loadOrderMetadata(nextRouteMatch.orderId);
  }

  private resetState(): void {
    this.requestSequence += 1;
    this.routeMatchState.set(null);
    this.gameTypeLabelState.set(null);
    this.tagsState.set([]);
    this.enabledOptionalSectionKeysState.set([]);
    this.loadingState.set(false);
  }

  private matchOrderRoute(url: string): OrderNavRouteMatch | null {
    const parsedUrl = this.router.parseUrl(url);
    const primarySegmentGroup = parsedUrl.root.children['primary'];
    const segments = primarySegmentGroup?.segments ?? [];

    const adminStandaloneRouteMatch = this.matchAdminStandaloneRoute(segments, parsedUrl.queryParams);
    if (adminStandaloneRouteMatch) {
      return adminStandaloneRouteMatch;
    }

    const [sectionSegment, idSegment] = segments;

    const sectionKey = sectionSegment?.path;
    if (!sectionKey || !isOrderNavSectionKey(sectionKey) || !idSegment?.path) {
      return null;
    }

    const orderId = Number(idSegment.path);
    if (!Number.isInteger(orderId) || orderId <= 0) {
      return null;
    }

    return {
      sectionKey,
      orderId
    };
  }

  private matchAdminStandaloneRoute(
    segments: { path: string }[],
    queryParams: Record<string, any>
  ): OrderNavRouteMatch | null {
    if (segments[0]?.path !== 'admin') {
      return null;
    }

    if (segments[1]?.path === 'stats-management' && segments[2]?.path === 'stat-explorer') {
      return this.buildAdminStandaloneRouteMatch(queryParams?.id, 'statsExplorer', 'Explorateur de stats');
    }

    if (segments[1]?.path !== 'orders' || segments.length < 4) {
      return null;
    }

    if (segments[2]?.path === 'overview') {
      return this.buildAdminStandaloneRouteMatch(segments[3]?.path, 'overview', 'Vue d\'ensemble');
    }

    if (segments[2]?.path === 'editJson') {
      return this.buildAdminStandaloneRouteMatch(segments[3]?.path, 'jsonEditor', 'Éditeur JSON');
    }

    if (segments[2]?.path === 'edit-sub-options') {
      return this.buildAdminStandaloneRouteMatch(segments[3]?.path, 'orderOptions', 'Options de commande');
    }

    return null;
  }

  private buildAdminStandaloneRouteMatch(
    rawOrderId: string | number | undefined,
    standalonePageKey: OrderNavStandalonePageKey,
    label: string
  ): OrderNavRouteMatch | null {
    const adminOrderId = Number(rawOrderId);
    if (!Number.isInteger(adminOrderId) || adminOrderId <= 0) {
      return null;
    }

    return {
      sectionKey: null,
      orderId: adminOrderId,
      label,
      standalonePageKey,
    };
  }

  private async loadOrderMetadata(orderId: number): Promise<void> {
    const requestId = ++this.requestSequence;
    this.loadingState.set(true);

    try {
      const [orderDetails, orderConfiguration, orderOptions] = await Promise.all([
        this.orderService.getOrderById(orderId) as Promise<ExperienceOrder>,
        this.orderService.getOrderConfiguration(orderId),
        this.orderService.getSubscribedOrderOptions(orderId)
      ]);

      if (!this.isLatestRequest(orderId, requestId)) {
        return;
      }

      const enabledOptionalSectionKeys = resolveEnabledOptionalOrderNavSectionKeys(orderOptions);
      const metadata: OrderNavMetadata = {
        gameTypeLabel: resolveGameTypeLabelFromConfiguration(orderConfiguration, orderDetails),
        tags: splitOrderTags(orderDetails?.tags),
        enabledOptionalSectionKeys
      };

      this.metadataCache.set(orderId, metadata);
      this.gameTypeLabelState.set(metadata.gameTypeLabel);
      this.tagsState.set(metadata.tags);
      this.enabledOptionalSectionKeysState.set(metadata.enabledOptionalSectionKeys);
    } catch (error) {
      if (!this.isLatestRequest(orderId, requestId)) {
        return;
      }

      console.warn('Unable to load side-menu order context', error);
      this.gameTypeLabelState.set(null);
      this.tagsState.set([]);
      this.enabledOptionalSectionKeysState.set([]);
    } finally {
      if (this.isLatestRequest(orderId, requestId)) {
        this.loadingState.set(false);
      }
    }
  }

  private isLatestRequest(orderId: number, requestId: number): boolean {
    return this.requestSequence === requestId && this.orderId() === orderId;
  }
}